import { hash } from '@ember/helper';
import { on } from '@ember/modifier';
import { guidFor } from '@ember/object/internals';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { pick } from '@nullvoxpopuli/ember-composable-helpers';
import { addYears } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import CopyButton from 'ember-cli-clipboard/components/copy-button';
import svgJar from 'ember-svg-jar/helpers/svg-jar';
import EmberTooltip from 'ember-tooltips/components/ember-tooltip';
import DatePickerDropdown from 'garaje/components/date-picker-dropdown';
import TimePicker from 'garaje/components/time-picker';
import UiInputField from 'garaje/components/ui-input-field';
import formatTimezoneAbbreviation from 'garaje/helpers/format-timezone-abbreviation';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type DateLimits from 'garaje/types/date-time/date-limits';
import { combineDateAndTime, updateDateToNewDay } from 'garaje/utils/date-and-time-utilities';
import { DEFAULT_TIMEZONE } from 'garaje/utils/date-fns-tz-utilities';
import isClipboardSupported from 'garaje/utils/is-clipboard-supported';

const TIME_FORMAT = 'h:mm aaa';

interface GroupInviteLinkFormSignature {
  Args: {
    additionalGuests: boolean;
    capacityLimit?: number | null;
    disabled?: boolean;
    expiresAt: Date | null;
    maxDate?: Date | null;
    onAdditionalGuestsChange: (checked: boolean) => void;
    onCapacityLimitChange: (capacityLimit: number) => void;
    onExpiresAtChange: (date: Date | null) => void;
    publicUrl?: string;
    showAdditionalGuests?: boolean;
    timezone?: string;
  };
  Element: HTMLDivElement;
}

export default class GroupInviteLinkForm extends Component<GroupInviteLinkFormSignature> {
  @service declare flashMessages: FlashMessagesService;

  elementId = guidFor(this);
  #today = new Date();

  get capacityLimitValue(): string {
    if (typeof this.args.capacityLimit === 'number') {
      return this.args.capacityLimit.toString();
    }

    return '';
  }

  get dateLimits(): DateLimits {
    return {
      // Can't choose a date before TODAY or after @maxDate
      startDate: this.#today.getTime() / 1000,
      endDate: this.maxDate.getTime() / 1000,
    };
  }

  get expiresAt(): Date {
    return this.args.expiresAt || this.#today;
  }

  // Return @maxDate if specified, otherwise 1 year from today
  get maxDate(): Date {
    return this.args.maxDate || addYears(this.#today, 1);
  }

  get selectedTime(): string {
    return formatInTimeZone(this.expiresAt, this.timezone, TIME_FORMAT);
  }

  get timezone(): string {
    return this.args.timezone ?? DEFAULT_TIMEZONE;
  }

  handleAdditionalGuestsChange = (checked: boolean): void => {
    this.args.onAdditionalGuestsChange(checked);
  };

  handleCapacityLimitChange = (value: string): void => {
    const capacityLimit = parseInt(value, 10);

    if (isNaN(capacityLimit) || capacityLimit < 0) return;

    this.args.onCapacityLimitChange(capacityLimit);
  };

  handleExpiresAtChange = (date: Date | null): void => {
    this.args.onExpiresAtChange(date);
  };

  handleTimeChange = (time: string): void => {
    const combinedDateTime = combineDateAndTime(this.expiresAt, time, this.timezone);
    this.args.onExpiresAtChange(combinedDateTime);
  };

  handleDateClicked = (date: Date): void => {
    const updatedDate = updateDateToNewDay(this.expiresAt, date, this.timezone);
    this.args.onExpiresAtChange(updatedDate);
  };

  onClipboardSuccess = (): void => {
    this.flashMessages.showAndHideFlash('success', 'Link copied to clipboard');
  };

  <template>
    <div class="flex flex-col gap-2" ...attributes data-test-group-invite-link-form>
      <div class="flex gap-6" data-test-expires-at>
        <div class="flex-1">
          <label for="groupInviteLinkExpiresAt">
            Registrations deadline date
          </label>
          <DatePickerDropdown
            @dateFormat="EEEE, MMM d, yyyy"
            @dateLimits={{this.dateLimits}}
            @dropdownOptions={{hash id="groupInviteLinkExpiresAt" triggerClass="!block !px-2" disabled=@disabled}}
            @endDate={{this.expiresAt}}
            @onDateSelected={{this.handleDateClicked}}
            @setEndDate={{this.handleDateClicked}}
            @setStartDate={{this.handleDateClicked}}
            @startDate={{this.expiresAt}}
            @timezone={{this.timezone}}
            data-test-group-invite-link-expires-at
          />
        </div>
        <div class="flex-1" data-test-group-invite-link-calendar-time>
          <label id="{{this.elementId}}-time">
            Registrations deadline time
            <span data-test-group-invite-link-timezone-abbreviation>
              {{#if this.timezone}}
                ({{formatTimezoneAbbreviation this.timezone}})
              {{/if}}
            </span>
          </label>
          <TimePicker
            @ariaLabelledBy="{{this.elementId}}-time"
            @isDisabled={{@disabled}}
            @onSelectTime={{this.handleTimeChange}}
            @time={{this.selectedTime}}
            data-test-group-invite-link-time-picker
          />
        </div>
      </div>

      <div class="flex gap-6">
        <div class="flex-1" data-test-capacity-limit>
          <UiInputField
            @label="Maximum visitors to self-register"
            @type="number"
            @min="0"
            @value={{this.capacityLimitValue}}
            @update={{this.handleCapacityLimitChange}}
            @disabled={{@disabled}}
            data-test-capacity-limit-input
          />
        </div>

        <div class="flex-1">
          {{#if @publicUrl}}
            <div class="flex gap-3 items-center">
              <UiInputField
                @label="Current registration link"
                @isReadonly={{true}}
                @value={{@publicUrl}}
                class="flex-1"
                data-test-public-url-input
              />
              {{#if (isClipboardSupported)}}
                <CopyButton
                  @clipboardText={{@publicUrl}}
                  @success={{this.onClipboardSuccess}}
                  class="mt-1"
                  data-test-group-invite-link-copy-url
                >
                  {{svgJar "copy" height="18" width="17"}}
                  <EmberTooltip @side="bottom">
                    Copy Link
                  </EmberTooltip>
                </CopyButton>
              {{/if}}
            </div>

          {{else}}
            &nbsp;
          {{/if}}
        </div>
      </div>

      {{#if @showAdditionalGuests}}
        <div class="flex items-start gap-2" data-test-additional-guests>
          <label class="checkbox flex items-start gap-2">
            <input
              type="checkbox"
              checked={{@additionalGuests}}
              disabled={{@disabled}}
              {{on "change" (pick "target.checked" this.handleAdditionalGuestsChange)}}
              class="mt-0.5"
              data-test-additional-guests-checkbox
            />
            <span class="text-sm font-medium text-carbon-50 leading-[1.1]">
              Allow visitors to add +1
            </span>
          </label>
        </div>
      {{/if}}
    </div>
  </template>
}
